<template>
  <div>
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 resource-title">
            {{ $t("Attendance Timing Schedule") }}
          </h5>
          <small class="text-muted">
            {{
              $t(
                "Temporary timing overrides for a date range (e.g. Ramzan)"
              )
            }}
          </small>
        </div>
      </div>

      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="loadSchedules"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <b-col cols="12" class="filter-toolbar timing-filter-toolbar d-flex align-items-center">
          <div
            class="filter-fields-group timing-filter-fields d-flex align-items-center flex-grow-1"
          >
            <b-form-input
              v-model="searchQuery"
              class="filter-search-input timing-filter-search"
              :placeholder="$t('Search') + '...'"
              @input="onSearchChange"
            />

            <v-select
              v-if="shouldShowCampusDropdown"
              v-model="filters.campusID"
              :options="filterCampusOptions"
              :reduce="(val) => val.id"
              label="branch"
              :clearable="true"
              :searchable="true"
              append-to-body
              :placeholder="$t('Campus')"
              class="timing-filter-select"
              @input="onFilterCampusChange"
            />

            <v-select
              v-model="filters.departmentID"
              :options="filterDepartmentOptions"
              :reduce="(val) => val.id"
              label="name"
              :clearable="true"
              :searchable="true"
              append-to-body
              :placeholder="$t('Department')"
              class="timing-filter-select"
              @input="onFilterDepartmentChange"
            />

            <div class="timing-filter-date">
              <flat-pickr
                v-model="filters.dateRange"
                :config="rangeDateConfig"
                class="form-control"
                :placeholder="$t('Date Range')"
                @on-change="onDateRangeChange"
              />
            </div>

            <b-button
              class="action-btn-filter filter-clear-btn timing-filter-clear"
              v-b-tooltip.hover.top
              :title="$t('Clear filters')"
              @click="clearFilters"
            >
              <span class="filter-clear-x" aria-hidden="true">×</span>
            </b-button>
          </div>

          <div class="filter-actions-group timing-filter-actions d-flex align-items-center">
            <b-button
              v-if="canAdd"
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              @click="openAddForm"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>{{ $t("Add Schedule") }}</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <b-table
          class="planner-list-table mt-1"
          :items="items"
          :fields="tableFields"
          :busy="dataLoading"
          show-empty
          :responsive="false"
          hover
          small
        >
          <template #empty>
            <h3 v-if="!dataLoading" class="mt-1 mb-1 text-center">
              {{ $t("No records found") }}
            </h3>
          </template>

          <template #table-busy>
            <div class="skeleton-table-wrap">
              <table class="skeleton-table">
                <tbody>
                  <tr
                    v-for="n in 5"
                    :key="'skeleton-' + n"
                    class="skeleton-row"
                  >
                    <td
                      v-for="(field, index) in tableFields"
                      :key="'skeleton-cell-' + index"
                      class="skeleton-cell"
                    >
                      <div class="skeleton-loader"></div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>

          <template #cell(index)="data">
            {{ (currentPage - 1) * perPage + data.index + 1 }}
          </template>

          <template #cell(title)="{ item }">
            <span class="font-weight-bold">{{ item.title || "—" }}</span>
          </template>

          <template #cell(dateRange)="{ item }">
            <b-badge variant="light-info">
              {{ formatDate(item.dtFrom) }} → {{ formatDate(item.dtTo) }}
            </b-badge>
          </template>

          <template #cell(campuses)="{ item }">
            <b-badge variant="light-primary">
              {{ formatCampusLabel(item.campusIDs) }}
            </b-badge>
          </template>

          <template #cell(departments)="{ item }">
            <div class="department-badges-wrap">
              <b-badge
                v-for="(name, depIndex) in getDepartmentNames(item)"
                :key="`${item.id || 'row'}-dep-${depIndex}`"
                variant="light-warning"
                class="department-badge"
              >
                {{ name }}
              </b-badge>
            </div>
          </template>

          <template #cell(staff)="{ item }">
            <b-badge variant="light-success">
              {{ formatStaffLabel(item.tIDs) }}
            </b-badge>
          </template>

          <template #cell(isActive)="{ item }">
            <b-form-checkbox
              :checked="!!item.isActive"
              switch
              :disabled="!canEdit || statusLoadingId === item.id"
              @change="toggleStatus(item, $event)"
            />
          </template>

          <template #head(actions)>
            <div class="text-center">
              <span>{{ $t("Actions") }}</span>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex justify-content-center">
              <b-button
                v-if="canEdit"
                variant="gradient-primary"
                v-b-tooltip.hover.top
                :title="$t('Edit')"
                class="action-btn-table mr-1"
                @click="openEditForm(item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                v-if="canDelete"
                variant="outline-danger"
                v-b-tooltip.hover.top
                :title="$t('Delete')"
                class="action-btn-outline delete-btn"
                @click="deleteSchedule(item)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Add / Edit Form Sidebar -->
    <b-sidebar
      id="sidebar-timing-schedule"
      bg-variant="white"
      v-model="formVisible"
      sidebar-class="sidebar-xl"
      right
      no-header
      backdrop
      shadow
      width="720px"
    >
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">
          {{ isEdit ? $t("Edit Timing Schedule") : $t("Add Timing Schedule") }}
        </h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="formVisible = false"
        />
      </div>

      <b-form class="p-2 timing-sidebar-form" @submit.prevent>
        <b-row>
          <b-col md="12">
            <b-form-group
              :label="$t('Title') + ' *'"
              :invalid-feedback="$t('Title is required')"
            >
              <b-form-input
                v-model.trim="form.title"
                :placeholder="$t('e.g. Ramzan Timing 2026')"
              />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group
              :label="$t('Date From') + ' *'"
              :invalid-feedback="$t('Date From is required')"
            >
              <flat-pickr
                v-model="form.dtFrom"
                class="form-control"
                :config="dateConfig"
                :placeholder="$t('Select date')"
              />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group
              :label="$t('Date To') + ' *'"
              :invalid-feedback="$t('Date To is required')"
            >
              <flat-pickr
                v-model="form.dtTo"
                class="form-control"
                :config="dateConfig"
                :placeholder="$t('Select date')"
              />
            </b-form-group>
          </b-col>

          <b-col md="12" v-if="shouldShowCampusDropdown">
            <b-form-group :label="$t('Campus')">
              <v-select
                multiple
                :closeOnSelect="false"
                v-model="form.campusIDs"
                :options="campusOptions"
                :reduce="(val) => val.id"
                label="branch"
                :clearable="false"
                :placeholder="$t('Select campus')"
                @input="onCampusChange"
              />
            </b-form-group>
          </b-col>

          <b-col md="12">
            <b-form-group :label="$t('Department')">
              <v-select
                multiple
                :closeOnSelect="false"
                v-model="form.departmentIDs"
                :options="departmentOptions"
                :reduce="(val) => val.id"
                label="name"
                :clearable="false"
                :placeholder="$t('Select department')"
                @input="onDepartmentChange"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div
          class="d-flex align-items-center justify-content-between mt-1 mb-1"
        >
          <h6 class="mb-0 font-weight-bold">{{ $t("Day Timings") }}</h6>
          <b-form-checkbox
            v-model="sameDays"
            switch
            inline
            v-b-tooltip.hover.bottomright
            :title="$t('Everyday schedule same as monday')"
            @input="applySameAsMonday"
          >
            {{ $t("Same as Monday") }}
          </b-form-checkbox>
        </div>

        <div
          v-for="(item, index) in form.timings"
          :key="item.day + '-' + index"
          class="timing-day-card mb-1"
        >
          <b-row>
            <b-col md="3">
              <div
                class="d-flex align-items-center justify-content-between pb-50"
              >
                <label class="bv-no-focus-ring col-form-label p-0 text-capitalize">
                  {{ item.day }}
                </label>
                <b-form-checkbox
                  v-model="item.dayOff"
                  switch
                  inline
                  class="custom-control-danger mr-0 ml-1"
                  v-b-tooltip.hover.bottomright
                  :title="$t('Day Off')"
                />
              </div>
            </b-col>
            <b-col md="3">
              <b-form-group :label="$t('Reporting Time')">
                <flat-pickr
                  v-model="item.reportingTime"
                  class="form-control"
                  :config="timeConfig"
                  :disabled="item.dayOff"
                  @on-close="onReportingChange(item)"
                />
              </b-form-group>
            </b-col>
            <b-col md="2">
              <b-form-group :label="$t('Grace Mins')">
                <b-form-input
                  type="number"
                  min="0"
                  v-model.number="item.graceMins"
                  :disabled="item.dayOff"
                  @input="onReportingChange(item)"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group :label="$t('Leaves at')">
                <flat-pickr
                  v-model="item.timeOut"
                  class="form-control"
                  :config="timeOutConfig"
                  :disabled="item.dayOff"
                  @on-close="applySameAsMondayFrom(item)"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <b-row class="mt-2 mb-2">
          <b-col md="12">
            <b-button
              variant="success"
              block
              :disabled="saving"
              @click="saveSchedule"
            >
              <b-spinner v-if="saving" small type="grow" />
              <span v-else>{{ isEdit ? $t("Update") : $t("Save") }}</span>
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BTable,
  BPagination,
  BBadge,
  BSpinner,
  BSidebar,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import Ripple from "vue-ripple-directive";
import { mapActions } from "vuex";
import Swal from "sweetalert2";

const WEEK_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const DEFAULT_REPORTING = "2026-01-01T08:00:00";
const DEFAULT_TIMEOUT = "2026-01-01T13:00:00";
const OFF_TIME = "2026-01-01T00:00:00";

export default {
  name: "StaffAttendanceTimingSchedule",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BTable,
    BPagination,
    BBadge,
    BSpinner,
    BSidebar,
    vSelect,
    flatPickr,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      rights: {},
      items: [],
      campuses: [],
      departments: [{ id: 0, name: "All Departments" }],
      filterDepartments: [{ id: 0, name: "All Departments" }],
      searchQuery: "",
      searchTimeout: null,
      filters: {
        campusID: null,
        departmentID: null,
        dateRange: null,
        dtFrom: "",
        dtTo: "",
      },
      dataLoading: true,
      saving: false,
      formVisible: false,
      isEdit: false,
      sameDays: true,
      statusLoadingId: null,
      currentPage: 1,
      perPage: 10,
      totalCount: 0,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      fields: [
        {
          label: "S.No",
          key: "index",
          thClass: "text-center",
          tdClass: "text-center",
        },
        { key: "title", label: "Title" },
        { key: "dateRange", label: "Date Range" },
        { key: "campuses", label: "Campuses" },
        { key: "departments", label: "Departments", tdClass: "departments-cell" },
        { key: "staff", label: "Staff" },
        {
          key: "isActive",
          label: "Status",
          thClass: "text-center",
          tdClass: "text-center",
        },
        {
          key: "actions",
          label: "Actions",
          thClass: "text-center",
          tdClass: "text-center",
        },
      ],
      form: this.createEmptyForm(),
      dateConfig: {
        altFormat: "d-M-Y",
        altInput: true,
        dateFormat: "Y-m-d",
        clickOpens: true,
      },
      rangeDateConfig: {
        mode: "range",
        altFormat: "d-M-Y",
        altInput: true,
        dateFormat: "Y-m-d",
        clickOpens: true,
      },
      timeConfig: {
        enableTime: true,
        noCalendar: true,
        altFormat: "h:i K",
        altInput: true,
        dateFormat: "Y-m-d\\TH:i:S",
        defaultHour: 8,
        defaultMinute: 0,
        clickOpens: true,
      },
      timeOutConfig: {
        enableTime: true,
        noCalendar: true,
        altFormat: "h:i K",
        altInput: true,
        dateFormat: "Y-m-d\\TH:i:S",
        defaultHour: 13,
        defaultMinute: 0,
        clickOpens: true,
      },
    };
  },
  computed: {
    displayedRange() {
      if (!this.totalCount) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    shouldShowCampusDropdown() {
      return Number(this.$store.state.userData?.cId) === 1;
    },
    campusOptions() {
      return [
        { id: 0, branch: this.$t("All Campuses") },
        ...(this.campuses || []),
      ];
    },
    filterCampusOptions() {
      return this.campuses || [];
    },
    departmentOptions() {
      return this.departments || [];
    },
    filterDepartmentOptions() {
      return (this.filterDepartments || []).filter((d) => d.id != 0);
    },
    canView() {
      return !!this.rights?.view;
    },
    canAdd() {
      return !!this.rights?.add;
    },
    canEdit() {
      return !!this.rights?.edit;
    },
    canDelete() {
      return !!this.rights?.delete;
    },
    tableFields() {
      return (this.fields || []).filter((field) => {
        if (field.key === "actions") {
          return this.canEdit || this.canDelete;
        }
        return true;
      });
    },
  },
  async created() {
    let right = {};
    (this.$store.state.menu || []).forEach((el) => {
      (el.children || []).forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    this.loadSchedules();

    await Promise.all([
      this.loadCampuses(),
      this.loadDepartments(this.$store.state.userData.cId),
      this.loadFilterDepartments(this.$store.state.userData.cId),
    ]);
  },
  methods: {
    ...mapActions(["get", "post", "delete"]),

    createEmptyForm() {
      return {
        id: 0,
        title: "",
        campusIDs: [0],
        departmentIDs: [0],
        dtFrom: "",
        dtTo: "",
        timings: WEEK_DAYS.map((day) => this.createDayTiming(day)),
      };
    },

    createDayTiming(day, source = null) {
      if (source) {
        return {
          day,
          reportingTime: source.reportingTime || DEFAULT_REPORTING,
          graceMins: Number(source.graceMins) || 0,
          timeIn: source.timeIn || source.reportingTime || DEFAULT_REPORTING,
          timeOut: source.timeOut || DEFAULT_TIMEOUT,
          dayOff: !!source.dayOff,
        };
      }
      if (day === "sunday") {
        return {
          day,
          reportingTime: OFF_TIME,
          graceMins: 0,
          timeIn: OFF_TIME,
          timeOut: OFF_TIME,
          dayOff: true,
        };
      }
      return {
        day,
        reportingTime: DEFAULT_REPORTING,
        graceMins: 15,
        timeIn: "2026-01-01T08:15:00",
        timeOut: DEFAULT_TIMEOUT,
        dayOff: false,
      };
    },

    addMinutesToIso(iso, mins) {
      try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        d.setMinutes(d.getMinutes() + (Number(mins) || 0));
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
          d.getDate()
        )}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      } catch (e) {
        return iso;
      }
    },

    onReportingChange(item) {
      if (!item || item.dayOff) return;
      item.timeIn = this.addMinutesToIso(
        item.reportingTime || DEFAULT_REPORTING,
        item.graceMins || 0
      );
      this.applySameAsMondayFrom(item);
    },

    applySameAsMonday() {
      if (!this.sameDays) return;
      const monday = this.form.timings.find((t) => t.day === "monday");
      if (!monday) return;
      this.form.timings = this.form.timings.map((t) => {
        if (t.day === "monday" || t.day === "sunday") return t;
        return {
          ...t,
          reportingTime: monday.reportingTime,
          graceMins: monday.graceMins,
          timeIn: monday.timeIn,
          timeOut: monday.timeOut,
          dayOff: monday.dayOff,
        };
      });
    },

    applySameAsMondayFrom(item) {
      if (this.sameDays && item && item.day === "monday") {
        this.applySameAsMonday();
      }
    },

    onCampusChange() {
      const ids = this.form.campusIDs || [];
      if (ids.at(-1) == 0) this.form.campusIDs = [0];
      else this.form.campusIDs = ids.filter((id) => id != 0);

      // All Campuses → current user campus; otherwise first selected campus
      const campusIds = this.form.campusIDs || [];
      const campusId =
        !campusIds.length || campusIds[0] == 0
          ? this.$store.state.userData.cId
          : campusIds[0];

      this.form.departmentIDs = [0];
      this.loadDepartments(campusId);
    },

    onDepartmentChange() {
      const ids = this.form.departmentIDs || [];
      if (ids.at(-1) == 0) this.form.departmentIDs = [0];
      else this.form.departmentIDs = ids.filter((id) => id != 0);
    },

    onFilterCampusChange() {
      // No campus → current user campus departments; else selected campus
      const campusId =
        this.filters.campusID == null || this.filters.campusID == 0
          ? this.$store.state.userData.cId
          : this.filters.campusID;

      this.filters.departmentID = null;
      this.loadFilterDepartments(campusId);
      this.onFilterChange();
    },

    onFilterDepartmentChange() {
      this.onFilterChange();
    },

    onFilterChange() {
      this.currentPage = 1;
      this.loadSchedules();
    },

    onDateRangeChange(selectedDates) {
      if (Array.isArray(selectedDates) && selectedDates.length === 2) {
        this.filters.dtFrom = this.toDateOnly(selectedDates[0]);
        this.filters.dtTo = this.toDateOnly(selectedDates[1]);
      } else if (Array.isArray(selectedDates) && selectedDates.length === 1) {
        this.filters.dtFrom = this.toDateOnly(selectedDates[0]);
        this.filters.dtTo = "";
        return;
      } else {
        this.filters.dtFrom = "";
        this.filters.dtTo = "";
      }
      this.onFilterChange();
    },

    clearFilters() {
      this.searchQuery = "";
      this.filters = {
        campusID: null,
        departmentID: null,
        dateRange: null,
        dtFrom: "",
        dtTo: "",
      };
      this.loadFilterDepartments(this.$store.state.userData.cId);
      this.currentPage = 1;
      this.loadSchedules();
    },

    formatDate(value) {
      if (!value) return "—";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    formatCampusLabel(campusIDs) {
      if (!campusIDs || campusIDs === "0") return this.$t("All Campuses");
      const ids = String(campusIDs)
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
      if (!ids.length) return this.$t("Current Campus");
      if (ids.length === 1) {
        const campus = this.campuses.find((c) => String(c.id) === ids[0]);
        return campus ? campus.branch || campus.name : `Campus ${ids[0]}`;
      }
      return `${ids.length} campuses`;
    },

    formatDepartmentLabel(item) {
      const names = this.getDepartmentNames(item);
      return names.length ? names.join(", ") : "—";
    },

    getDepartmentNames(item) {
      if (!item) return [];

      if (item.departmentNames && String(item.departmentNames).trim()) {
        return String(item.departmentNames)
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean);
      }

      const ids = String(item.departmentIDs || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);

      if (!ids.length || ids[0] === "0") {
        return [this.$t("All Departments")];
      }

      return ids
        .map((id) => {
          const dep = this.departments.find((d) => String(d.id) === id);
          return dep ? dep.name : id;
        })
        .filter(Boolean);
    },

    formatStaffLabel(tIDs) {
      if (!tIDs || tIDs === "0") return this.$t("All Staff");
      const ids = String(tIDs)
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
      return ids.length ? `${ids.length} staff` : this.$t("All Staff");
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadSchedules();
    },

    onSearchChange() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadSchedules();
      }, 400);
    },

    async loadCampuses() {
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "campuses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        });
        this.campuses = Array.isArray(res) ? res : [];
      } catch (e) {
        this.campuses = [];
      }
    },

    async loadDepartments(campusId) {
      const cId = campusId || this.$store.state.userData.cId;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "StaffDepartments?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            cId,
          token: this.$store.state.userData.token,
        });
        const list = (Array.isArray(res) ? res : []).map((d) => ({
          ...d,
          name: d.name || d.department || `Department ${d.id}`,
        }));
        this.departments = [{ id: 0, name: "All Departments" }, ...list];
      } catch (e) {
        this.departments = [{ id: 0, name: "All Departments" }];
      }
    },

    async loadFilterDepartments(campusId) {
      const cId = campusId || this.$store.state.userData.cId;
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "StaffDepartments?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            cId,
          token: this.$store.state.userData.token,
        });
        const list = (Array.isArray(res) ? res : []).map((d) => ({
          ...d,
          name: d.name || d.department || `Department ${d.id}`,
        }));
        this.filterDepartments = [{ id: 0, name: "All Departments" }, ...list];
      } catch (e) {
        this.filterDepartments = [{ id: 0, name: "All Departments" }];
      }
    },

    async loadSchedules() {
      this.dataLoading = true;
      try {
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: String(this.$store.state.userData.cId),
          pageNumber: String(this.currentPage),
          pageSize: String(this.perPage),
        });

        if (this.searchQuery && this.searchQuery.trim()) {
          params.append("search", this.searchQuery.trim());
        }

        // Campus filter (same rules as add/edit)
        if (this.shouldShowCampusDropdown) {
          if (this.filters.campusID != null && this.filters.campusID != 0) {
            params.append("campusIDs", String(this.filters.campusID));
          }
        }

        // Department filter
        if (this.filters.departmentID != null && this.filters.departmentID != 0) {
          params.append("departmentIDs", String(this.filters.departmentID));
          const dep = (this.filterDepartments || []).find(
            (d) => d.id == this.filters.departmentID
          );
          if (dep && dep.name) {
            params.append("departmentNames", dep.name);
          }
        }

        if (this.filters.dtFrom) {
          params.append("dtFrom", this.filters.dtFrom);
        }
        if (this.filters.dtTo) {
          params.append("dtTo", this.filters.dtTo);
        }

        const res = await this.get({
          url:
            this.$store.state.domain +
            "StaffAttendanceTimingSchedule/GetByPagination?" +
            params.toString(),
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        if (res && Array.isArray(res.data)) {
          this.items = res.data;
          this.totalCount = Number(res.totalCount) || res.data.length;
        } else if (Array.isArray(res)) {
          this.items = res;
          this.totalCount = res.length;
        } else {
          this.items = [];
          this.totalCount = 0;
        }
      } catch (e) {
        console.error(e);
        this.items = [];
        this.totalCount = 0;
        this.$bvToast.toast(this.$t("Failed to load schedules"), {
          title: this.$t("Error"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.dataLoading = false;
      }
    },

    openAddForm() {
      if (!this.canAdd) return;
      this.isEdit = false;
      this.form = this.createEmptyForm();
      this.sameDays = true;
      this.formVisible = true;
      // Default (All Campuses): load departments for current user campus
      this.loadDepartments(this.$store.state.userData.cId);
    },

    async openEditForm(item) {
      if (!this.canEdit) return;
      if (!item || !item.id) return;
      this.isEdit = true;
      this.formVisible = true;
      this.sameDays = false;

      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "StaffAttendanceTimingSchedule/Load?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&id=" +
            item.id,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        const payload = res?.data || res || {};
        const schedule = payload.schedule || payload;
        const timings = Array.isArray(payload.timings)
          ? payload.timings
          : Array.isArray(res?.timings)
          ? res.timings
          : [];

        const campusIds = this.parseIdList(schedule.campusIDs, true);
        const deptIds = this.parseIdList(schedule.departmentIDs, true);
        const resolvedCampusIds = campusIds.length ? campusIds : [0];
        const campusIdForDeps =
          !resolvedCampusIds.length || resolvedCampusIds[0] == 0
            ? this.$store.state.userData.cId
            : resolvedCampusIds[0];

        await this.loadDepartments(campusIdForDeps);

        this.form = {
          id: schedule.id || item.id,
          title: schedule.title || "",
          campusIDs: resolvedCampusIds,
          departmentIDs: deptIds.length ? deptIds : [0],
          dtFrom: this.toDateOnly(schedule.dtFrom),
          dtTo: this.toDateOnly(schedule.dtTo),
          timings: WEEK_DAYS.map((day) => {
            const found = timings.find(
              (t) => String(t.day || "").toLowerCase() === day
            );
            return this.createDayTiming(day, found);
          }),
        };
      } catch (e) {
        console.error(e);
        this.$bvToast.toast(this.$t("Failed to load schedule"), {
          title: this.$t("Error"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        this.formVisible = false;
      }
    },

    parseIdList(value, allowZero) {
      if (value === 0 || value === "0") return allowZero ? [0] : [];
      if (value == null || value === "") return allowZero ? [0] : [];
      return String(value)
        .split(",")
        .map((x) => Number(x.trim()))
        .filter((n) => !Number.isNaN(n) && (allowZero || n !== 0));
    },

    toDateOnly(value) {
      if (!value) return "";
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, "0");
        const d = String(value.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      }
      if (typeof value === "string" && value.includes("T")) {
        return value.split("T")[0];
      }
      return String(value).slice(0, 10);
    },

    buildCampusIDsPayload() {
      if (!this.shouldShowCampusDropdown) return null;
      const ids = this.form.campusIDs || [];
      if (!ids.length || ids[0] == 0) return "0";
      return ids.join(",");
    },

    buildDepartmentPayload() {
      const ids = this.form.departmentIDs || [];
      if (!ids.length || ids[0] == 0) {
        return { departmentIDs: "0", departmentNames: "" };
      }
      const names = this.departments
        .filter((d) => ids.includes(d.id) && d.id != 0)
        .map((d) => d.name)
        .filter(Boolean);
      return {
        departmentIDs: ids.join(","),
        departmentNames: names.join(","),
      };
    },

    validateForm() {
      if (!this.form.title || !String(this.form.title).trim()) {
        this.$bvToast.toast(this.$t("Please enter title"), {
          title: this.$t("Validation"),
          variant: "warning",
          toaster: "b-toaster-top-center",
        });
        return false;
      }
      if (!this.form.dtFrom || !this.form.dtTo) {
        this.$bvToast.toast(this.$t("Please select date range"), {
          title: this.$t("Validation"),
          variant: "warning",
          toaster: "b-toaster-top-center",
        });
        return false;
      }
      if (this.form.dtFrom > this.form.dtTo) {
        this.$bvToast.toast(this.$t("Date From must be before Date To"), {
          title: this.$t("Validation"),
          variant: "warning",
          toaster: "b-toaster-top-center",
        });
        return false;
      }
      if (
        this.shouldShowCampusDropdown &&
        (!this.form.campusIDs || !this.form.campusIDs.length)
      ) {
        this.$bvToast.toast(this.$t("Please select campus"), {
          title: this.$t("Validation"),
          variant: "warning",
          toaster: "b-toaster-top-center",
        });
        return false;
      }
      return true;
    },

    async saveSchedule() {
      if (this.isEdit ? !this.canEdit : !this.canAdd) return;
      if (!this.validateForm()) return;

      this.saving = true;
      try {
        const deptPayload = this.buildDepartmentPayload();
        const campusIDs = this.buildCampusIDsPayload();
        const body = {
          id: this.form.id || 0,
          title: this.form.title.trim(),
          departmentIDs: deptPayload.departmentIDs,
          departmentNames: deptPayload.departmentNames,
          tIDs: "0",
          dtFrom: this.form.dtFrom,
          dtTo: this.form.dtTo,
          timings: this.form.timings.map((t) => {
            const dayOff = !!t.dayOff;
            const reportingTime = dayOff
              ? OFF_TIME
              : t.reportingTime || DEFAULT_REPORTING;
            const graceMins = dayOff ? 0 : Number(t.graceMins) || 0;
            const timeIn = dayOff
              ? OFF_TIME
              : this.addMinutesToIso(reportingTime, graceMins);
            return {
              day: t.day,
              reportingTime,
              graceMins,
              timeIn,
              timeOut: dayOff ? OFF_TIME : t.timeOut || DEFAULT_TIMEOUT,
              dayOff,
            };
          }),
        };
        if (campusIDs != null) body.campusIDs = campusIDs;

        const status = await this.post({
          url:
            this.$store.state.domain +
            "StaffAttendanceTimingSchedule/Save?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&userID=" +
            this.$store.state.userData.userID,
          body,
          context: this,
          token: this.$store.state.userData.token,
          message: this.isEdit
            ? "Schedule updated successfully"
            : "Schedule saved successfully",
        });

        if (status) {
          this.formVisible = false;
          await this.loadSchedules();
        }
      } finally {
        this.saving = false;
      }
    },

    async toggleStatus(item, isActive) {
      if (!this.canEdit) return;
      if (!item || !item.id) return;
      this.statusLoadingId = item.id;
      const previous = !!item.isActive;
      this.$set(item, "isActive", !!isActive);
      try {
        const status = await this.post({
          url:
            this.$store.state.domain +
            "StaffAttendanceTimingSchedule/SetStatus?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&id=" +
            item.id +
            "&userID=" +
            this.$store.state.userData.userID +
            "&isActive=" +
            (!!isActive),
          body: null,
          context: this,
          token: this.$store.state.userData.token,
          message: isActive
            ? "Schedule activated"
            : "Schedule deactivated",
        });
        if (!status) this.$set(item, "isActive", previous);
      } catch (e) {
        this.$set(item, "isActive", previous);
      } finally {
        this.statusLoadingId = null;
      }
    },

    async deleteSchedule(item) {
      if (!this.canDelete) return;
      if (!item || !item.id) return;
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("This schedule will be deleted permanently."),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("Yes, delete it"),
        cancelButtonText: this.$t("Cancel"),
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      });
      if (!result.isConfirmed) return;

      const status = await this.delete({
        url:
          this.$store.state.domain +
          "StaffAttendanceTimingSchedule/Delete?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId +
          "&id=" +
          item.id +
          "&userID=" +
          this.$store.state.userData.userID,
        context: this,
        token: this.$store.state.userData.token,
        message: "Schedule deleted successfully",
      });
      if (status) await this.loadSchedules();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../planner/plannerListTable.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.timing-filter-toolbar {
  flex-wrap: nowrap !important;
  gap: 0.5rem;
  min-width: 0;
}

.timing-filter-fields {
  gap: 0.5rem;
  min-width: 0;
  flex-wrap: nowrap !important;
  width: auto !important;
}

.timing-filter-search {
  flex: 1 1 0;
  min-width: 120px;
  max-width: none;
  height: 38px !important;
}

.timing-filter-select {
  flex: 1 1 0;
  min-width: 140px;
  max-width: none;

  ::v-deep .vs__dropdown-toggle {
    min-height: 38px;
    height: 38px;
    border-radius: 12px;
    border: 1.5px solid #e0d8ff;
    background: #fff;
  }

  ::v-deep .vs__selected {
    margin-top: 0;
    margin-bottom: 0;
  }

  ::v-deep .vs__actions {
    padding-top: 0;
    padding-bottom: 0;
  }

  ::v-deep .vs__dropdown-menu {
    z-index: 3000;
  }
}

.timing-filter-date {
  flex: 1 1 0;
  min-width: 160px;
  max-width: none;

  ::v-deep .flatpickr-wrapper,
  ::v-deep .flatpickr-input,
  ::v-deep input.form-control {
    width: 100% !important;
  }

  ::v-deep .form-control {
    height: 38px !important;
    min-height: 38px !important;
    border-radius: 12px !important;
    border: 1.5px solid #e0d8ff !important;
    background: #fff !important;
    font-weight: 500;
    color: #2d1b69;
  }
}

.timing-filter-clear {
  flex: 0 0 38px !important;
  width: 38px !important;
  min-width: 38px !important;
  max-width: 38px !important;
  height: 38px !important;
  margin: 0 !important;
}

.timing-filter-actions {
  flex-shrink: 0;
  margin-left: 0 !important;
  width: auto !important;
}

.timing-sidebar-form {
  ::v-deep .flatpickr-input[readonly],
  ::v-deep .flatpickr-input ~ .form-control[readonly],
  ::v-deep .flatpickr-human-friendly[readonly] {
    background-color: #fff !important;
    opacity: 1 !important;
    cursor: pointer;
  }

  ::v-deep .flatpickr-calendar {
    z-index: 1100;
  }
}

.timing-day-card {
  border: 1px solid #ebe7ff;
  border-radius: 12px;
  padding: 0.75rem 0.85rem 0.15rem;
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
}

.skeleton-table-wrap {
  width: 100%;
}

.skeleton-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.skeleton-row {
  animation: skeletonPulse 1.5s ease-in-out infinite;
}

.skeleton-cell {
  padding: 0.75rem 0.85rem !important;
}

.skeleton-loader {
  height: 16px;
  border-radius: 8px;
  width: 100%;
  background: linear-gradient(90deg, #f0f0f0, #f8f8f8, #f0f0f0);
  background-size: 200% 100%;
  animation: skeletonShimmer 1.4s linear infinite;
}

@keyframes skeletonPulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

@keyframes skeletonShimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

::v-deep .b-table-busy-slot > td {
  padding: 0 !important;
}

::v-deep .departments-cell {
  white-space: normal !important;
  max-width: 320px;
}

.department-badges-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: flex-start;
}

.department-badge {
  white-space: normal;
  word-break: break-word;
  font-weight: 500;
  line-height: 1.35;
}
</style>
